import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import baseURL from "../../config/api";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import useAuth from "../../hook/useAuth";
import { formatUSPhone } from "../../utils/phoneFormatter";
import useGroup from "../../hook/useGroup";
import useConfirm from "../../hook/useConfirm";
import Cookies from "js-cookie";

const PAGE_SIZE = 10;

const LeadershipGroupLeaders = () => {
    const token = Cookies.get("token-my-cdir");
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { auth }: any = useAuth();
    const { selectedGroupId }: any = useGroup();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const leadershipGroup = location.state?.leadershipGroup;
    const [leaders, setLeaders] = useState<any[]>([]);
    const [options, setOptions] = useState<any[]>([]);
    const [selectedLeader, setSelectedLeader] = useState<any>(null);
    const [optionPage, setOptionPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [adding, setAdding] = useState(false);
    const [removingMap, setRemovingMap] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const [churchName, setChurchName] = useState<string>("");

    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        size: 10,
    });

    const [totalLength, setTotalLength] = useState(0);
    const [showEntries, setShowEntries] = useState(10);

    useEffect(() => {
        if (selectedGroupId) {
            fetch(
                `${baseURL}group/${selectedGroupId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token?.toString()}`
                    }
                }
            )
                .then((res) => res.json())
                .then((data) => {
                    setChurchName(data?.name);
                })
                .catch((error) => {
                    console.log(error);
                    setChurchName("");
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId])

    const isLeaderInGroup = (leader: any) => {
        return leader.leadershipGroups?.some(
            (lg: any) => lg.leadershipGroupId === Number(id)
        );
    };

    const fetchLeaders = async () => {
        if (!selectedGroupId) return;

        setLoading(true);

        try {
            const res = await fetch(
                `${baseURL}leaderships/GetLeadershipsPagingByLeadershipGroupId/${id},${selectedGroupId},id,asc,${paginationModel.page},${paginationModel.size}`,
                {
                    headers: {
                        Authorization: `Bearer ${token?.toString()}`
                    }
                }
            );

            const data = await res.json();

            const filtered =
                data?.leaderships?.filter((l: any) => isLeaderInGroup(l)) || [];

            setLeaders(filtered);
            setTotalLength(data?.count || 0);

        } catch (err) {
            console.error(err);
            setLeaders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeaders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel.page, paginationModel.size, selectedGroupId]);

    const fetchOptions = async (page = 0, name = "", replace = false) => {
        if (!selectedGroupId || loadingOptions) return;

        setLoadingOptions(true);

        try {
            let url = "";

            if (name?.trim() === "") {
                url = `${baseURL}leaderships/GetLeadershipsSortedByName/${selectedGroupId},${page},${PAGE_SIZE}`;
            } else {
                url = `${baseURL}leaderships/GetLeadershipsSortedByNameAndByCustomerNumber?customerName=${name}&groupId=${selectedGroupId}&page=${page}&size=${PAGE_SIZE}`;
            }

            const res = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token?.toString()}`
                }
            });
            const data = await res.json();

            const newOptions = (data?.leaderships || []).map((item: any) => ({
                value: item.id,
                label: `${item.firstName} ${item.lastName}`,
                designation: item.designation,
            }));

            setOptions((prev) => {
                let updatedOptions;
                if (replace || page === 0) {
                    updatedOptions = newOptions;
                } else {
                    const existing = new Set(prev.map((p) => p.value));
                    const filtered = newOptions.filter((o: any) => !existing.has(o.value));
                    updatedOptions = [...prev, ...filtered];
                }

                // compute hasMore after merging
                const totalCount = data?.count || 0;
                setHasMore(updatedOptions.length < totalCount);

                return updatedOptions;
            });

            const totalCount = data?.count || 0;
            const currentLoaded = (replace || page === 0 ? 0 : options.length) + newOptions.length;
            setHasMore(currentLoaded < totalCount);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingOptions(false);
        }
    }

    useEffect(() => {
        setOptionPage(0);
        setOptions([]);
        setHasMore(true);
        fetchOptions(0, searchValue, true);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId]);

    const handleInputChange = (input: string, actionMeta: any) => {
        if (actionMeta.action === "input-change") {
            setSearchValue(input);

            if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

            debounceTimeoutRef.current = setTimeout(() => {
                setOptionPage(0);
                setOptions([]);
                setHasMore(true);
                fetchOptions(0, input, true);
            }, 1000);
        }
    }

    const addLeader = async () => {
        if (!selectedLeader) return;

        setAdding(true);

        try {
            const res = await fetch(`${baseURL}leaderships/DoesLeadershipAlreadyExist/${selectedLeader?.value},${id}`, {
                headers: {
                    Authorization: `Bearer ${token?.toString()}`
                }
            });
            const isExist = await res.json();

            if (!isExist) {
                await fetch(
                    `${baseURL}leadershipGroupMappings/CreateBulkLeadershipGroupMapping`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token?.toString()}`
                        },
                        body: JSON.stringify([
                            {
                                leadershipGroupId: Number(id),
                                leadershipId: selectedLeader.value,
                            },
                        ]),
                    }
                );

                fetchOptions(0, searchValue, true);

                setSelectedLeader(null);
                setAdding(false);
                fetchLeaders();
            } else {
                setConfirmData({
                    title: "Warning",
                    summary: `${selectedLeader?.label} already exists.`,
                    confirmBtn: "Ok",
                    isCancelBtn: true,
                    isConfirm: (cb: any) => {
                        cb();
                    }
                });
                setConfirmPopup(true);
                setSelectedLeader(null);
                setSearchValue("");
                fetchOptions(0, "", true);
                setAdding(false);
            }
        } catch (err) {
            setAdding(false);
            console.error(err);
        }
    };

    const removeLeader = async (leaderId: number) => {
        const key = `${id}-${leaderId}`;
        setRemovingMap((p: any) => ({ ...p, [key]: true }));

        try {
            await fetch(
                `${baseURL}leadershipGroupMappings/DeleteLeadershipGroupMappingWithLeadershipIdAndLeadershipGroupId/${leaderId},${id},${auth?.id}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token?.toString()}`
                    }
                }
            );

            setPaginationModel((prev) => ({ ...prev, page: 0 }));

            fetchLeaders();
            fetchOptions(0, searchValue, true);

            setRemovingMap((p: any) => ({ ...p, [key]: false }));
        } catch (err) {
            console.error(err);
        }
    };

    const handlePrint = () => window.print();

    return (
        <>
            <section className="p-4 xm:p-6">
                {/* Header */}
                <div className="flex flex-col xm:flex-row xm:justify-between gap-4 mb-6">
                    <h1 className="text-xl xm:text-2xl font-bold text-secondary">
                        {leadershipGroup?.name} Leaders
                    </h1>

                    <div className="flex flex-col xm:flex-row gap-2 xm:gap-3 w-full xm:w-auto">
                        <button
                            onClick={handlePrint}
                            className="bg-green-500 text-white px-4 py-2 rounded w-full xm:w-auto"
                        >
                            Download PDF
                        </button>

                        <button
                            onClick={() => navigate(-1)}
                            className="bg-gray-400 text-white px-4 py-2 rounded w-full xm:w-auto"
                        >
                            Back
                        </button>
                    </div>
                </div>

                {/* Select + Add */}
                <div className="flex flex-col xm:flex-row gap-3 mb-6">
                    <div className="flex-1">
                        <Select
                            options={options}
                            value={selectedLeader}
                            onChange={(val) => setSelectedLeader(val)}
                            onInputChange={handleInputChange}
                            onMenuScrollToBottom={() => {
                                if (!loadingOptions && hasMore) {
                                    const nextPage = optionPage + 1;
                                    setOptionPage(nextPage);
                                    fetchOptions(nextPage, searchValue, false);
                                }
                            }}
                            isLoading={loadingOptions}
                            placeholder="Select Leader..."
                            filterOption={null}
                            noOptionsMessage={() => {
                                if (loadingOptions) return "Loading...";
                                if (hasMore) return "Scroll to load more...";
                                return "No more leaders";
                            }}
                        />
                    </div>

                    <button
                        onClick={addLeader}
                        disabled={!selectedLeader || adding}
                        className="bg-secondary text-white px-4 py-2 rounded w-full xm:w-auto"
                    >
                        {adding ? "Adding..." : "Add"}
                    </button>
                </div>

                {/* Cards */}
                {!loading && leaders.length > 0 ? (
                    <div className="grid grid-cols-1 xm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {leaders.map((l: any) => {
                            const key = `${id}-${l.id}`;

                            return (
                                <div
                                    key={l.id}
                                    className="w-[230px] max-xm:w-full flex flex-col xs:flex-row border rounded-xl overflow-hidden shadow-sm"
                                >
                                    {/* Image */}

                                    <img
                                        src={
                                            l.imageUrl
                                                ? `${baseURL}commonFile/Get?name=${l.imageUrl}`
                                                : "/images/img_placeholder1.jpg"
                                        }
                                        alt="leaderships"
                                        className="xm:w-[230px] xm:h-[200px] max-xm:w-full object-cover object-top"
                                    />


                                    {/* Content */}
                                    <div className="p-3 xm:p-4 flex-1">
                                        <p className="font-bold text-sm xm:text-base">
                                            {l.firstName} {l.lastName}
                                        </p>

                                        <p className="text-xs xm:text-sm">{l.designation}</p>
                                        <p className="text-xs xm:text-sm break-all">{l.email}</p>
                                        <p className="text-xs xm:text-sm">{l.phoneNumber}</p>

                                        <button
                                            className={`text-xs mt-2 ${removingMap[key]
                                                ? "text-gray-400"
                                                : "text-red-500"
                                                }`}
                                            onClick={() => removeLeader(l.id)}
                                        >
                                            {removingMap[key] ? "Removing..." : "Remove"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center mt-10 text-gray-400">
                        No leaders found
                    </div>
                )}

                {/* Pagination */}
                {totalLength > 0 && (
                    <div className="mt-6 flex flex-col xm:flex-row items-center justify-between gap-3">
                        <Pagination
                            totalLength={totalLength}
                            paginationModel={paginationModel}
                            setPaginationModel={setPaginationModel}
                            showEntries={showEntries}
                            setShowEntries={(val) => {
                                setShowEntries(val);
                                setPaginationModel({ page: 0, size: val });
                            }}
                        />
                    </div>
                )}
            </section>

            {/* Print Section (unchanged) */}
            <div
                id="print-section"
                className="hidden print:block"
            >
                <div className="border-b-2 border-secondary pb-3 mb-5">
                    <div className="w-full flex justify-center">
                        <h1 className="text-lg font-bold text-secondary">
                            {churchName}
                        </h1>
                    </div>
                    <div className="flex justify-between items-center mt-2 text-xs text-gray-600">
                        <span className="font-semibold text-[0.875rem]">
                            {leadershipGroup?.name} - Leaders
                        </span>

                        <span>
                            {new Date().toLocaleString("en-US", {
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                            })}
                        </span>
                    </div>
                </div>

                {leaders.length === 0 ? (
                    <div className="w-full text-center mt-10">No data available</div>
                ) : (
                    <table className="w-full border border-collapse text-sm">
                        <thead>
                            <tr className="bg-gray-200">
                                <th className="border p-2">#</th>
                                <th className="border p-2">Full Name</th>
                                <th className="border p-2">Email</th>
                                <th className="border p-2">Phone</th>
                            </tr>
                        </thead>

                        <tbody>
                            {leaders.map((m, i) => (
                                <tr key={m.id}>
                                    <td className="border p-2">{i + 1}</td>
                                    <td className="border p-2">
                                        {m.firstName} {m.lastName}
                                    </td>
                                    <td className="border p-2">{m.email}</td>
                                    <td className="border p-2">
                                        {formatUSPhone(m.phoneNumber)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
};

export default LeadershipGroupLeaders;